import * as React from 'react';
import { memo, useMemo } from 'react';
import { cn } from '@restworld/utils-common';

import { Variable } from '@restworld/utility-types';

import { IconComponentType } from '../..';
import { InputProps, SkeletonLoading, TextInput } from '../..';
import { focusRing } from '../styles/focus-ring';

export const RadioGroup = memo((props: IRadioGroupProps) => {
  const { helperText, orientation, className, mainTitle, subTitle, ...rest } = props;

  return (
    <div className={cn('flex gap-6 flex-col w-full', className)}>
      {mainTitle ? <p className="text-lg sm:text-xl font-semibold text-center leading-7">{mainTitle}</p> : null}
      {subTitle ? <p className="text-sm font-normal text-neutral-500 text-center">{subTitle}</p> : null}
      <div
        className={cn(
          'mx-auto w-full overflow-y-auto md:max-h-80 flex flex-col gap-2',
          orientation === 'horizontal' && 'max-w-full'
        )}
      >
        <RadioComponent orientation={orientation} {...rest} />
      </div>
      {helperText}
    </div>
  );
});

export default RadioGroup;

const RadioComponent = memo((props: IRadioGroupProps) => {
  const { options, onChange, testId, isFullWidth, orientation = 'vertical', isLoading, disabled, ...rest } = props;

  return (
    <ul
      role="radiogroup"
      className={
        orientation === 'horizontal'
          ? // Below `sm` a full-width row may break onto a second line: three options whose longest
            // words do not fit side by side (e.g. on a 360px phone) would otherwise widen the page.
            `flex flex-row ${isFullWidth ? 'justify-between flex-wrap sm:flex-nowrap gap-1' : 'flex-wrap gap-3'}`
          : 'flex flex-col gap-3'
      }
      data-cy={testId}
    >
      {isLoading && (
        <div className="flex flex-col gap-3">
          <SkeletonLoading variant="input" />
          <SkeletonLoading variant="input" />
          <SkeletonLoading variant="input" />
        </div>
      )}
      {options ? (
        options.map((option, index) => (
          <Radio
            orientation={orientation}
            key={option.id + index}
            option={option}
            index={index}
            isFullWidth={isFullWidth}
            onChange={onChange}
            disabled={disabled}
            {...rest}
          />
        ))
      ) : (
        <SkeletonLoading variant="text" />
      )}
    </ul>
  );
});

export const Radio = ({
  option,
  index,
  value,
  borderless,
  showVerticalLineBelow,
  id,
  inlineTextInputProps,
  testId,
  orientation,
  customOptionStyle,
  hideIndicator,
  isFullWidth,
  returnKey = 'id',
  onChange,
  disabled: groupDisabled,
}: RadioOptionProps) => {
  const returnKeyWithType = returnKey as keyof typeof option;
  const optionValue = option[returnKeyWithType];
  const checked = useMemo(() => value === optionValue, [optionValue, value]);
  const returnId = useMemo(
    () => (returnKeyWithType && optionValue ? `${optionValue?.toString()}-${id}` : option.id),
    [id, option.id, optionValue, returnKeyWithType]
  );
  const hasInput = useMemo(() => value === 'other' && option.id === 'other', [option.id, value]);
  const Icon = option.Icon;
  // Group-level `disabled` (RadioGroup) or the single option's own: production passed it to
  // HeadlessRadioGroup, which supported the group disabled; the <ul> that replaced it does not.
  const disabled = groupDisabled || option.disabled;

  const otherOptionInput = useMemo(() => {
    if (inlineTextInputProps && hasInput) return <TextInput type="text" name="other" {...inlineTextInputProps} />;
    else return null;
  }, [hasInput, inlineTextInputProps]);

  return (
    <li
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled}
      data-cy={testId || `${option.id}-${id}`}
      onKeyDown={(e) => {
        if (value === 'other') e.stopPropagation();
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!disabled && onChange) onChange(optionValue as string);
        }
      }}
      id={returnId}
      tabIndex={disabled ? -1 : 0}
      // Full-width HORIZONTAL groups use w-fit (as master did) so the flex-1 columns size evenly;
      // w-full made the narrowest column collapse and its label wrap. Vertical groups keep w-full.
      className={cn(isFullWidth && orientation !== 'horizontal' ? 'w-full' : 'w-fit', isFullWidth && 'flex-1')}
      onClick={() => {
        if (!disabled && onChange) onChange(optionValue as string);
      }}
    >
      <div
        id={`radiogroup-StyledOption-${option.label}`}
        className={cn(
          `group ${focusRing}`,
          // min-h (not a hard !h-12): options with a title + description must grow to fit instead of clipping.
          'border border-neutral-90 py-2 px-3 sm:px-3 rounded-lg text-neutral-700 flex gap-3 items-center justify-start cursor-pointer min-h-[3rem]',
          'w-full',
          value
            ? checked
              ? 'bg-purple-50 text-purple-700 border-purple-700 hover:bg-purple-50'
              : 'bg-neutral-10 hover:bg-neutral-30'
            : option.colorStyles
            ? option.colorStyles
            : checked
            ? 'bg-purple-50 text-purple-700 border-purple-700 hover:bg-purple-50'
            : 'bg-neutral-10 hover:bg-neutral-30',
          borderless && 'bg-inherit border-none hover:bg-inherit text-neutral-700 gap-0',
          showVerticalLineBelow && 'border-gray-300 border-b px-0',
          hasInput && 'items-start',
          // h-full: side-by-side boxes stay the same height when one label wraps on a narrow screen.
          orientation === 'horizontal' && 'min-w-fit justify-center h-full',
          disabled && !borderless && 'bg-neutral-40 pointer-events-none border-neutral-40 text-neutral-200',
          disabled && borderless && 'text-neutral-100',
          customOptionStyle
        )}
      >
        {hideIndicator ? null : (
          <span
            className={cn(
              'relative grid place-items-center',
              borderless && 'h-10 w-10 min-h-[40px] min-w-[40px] group-hover:text-purple-700'
            )}
          >
            {Icon ? (
              <Icon className="h-4 w-4 min-h-[4px] min-w-[4px] relative z-10" />
            ) : (
              <span
                className={cn(
                  'rounded-full border !h-4 !w-4 min-h-[16px] min-w-[16px] border-neutral-100 grid place-items-center relative z-10',
                  checked && !disabled && 'border-purple-700',
                  disabled && checked && !borderless && 'border-neutral-90 border-[4px]',
                  borderless && 'group-hover:border-purple-700',
                  disabled && borderless && checked && 'bg-inherit',
                  disabled && borderless && !checked && 'bg-neutral-40'
                )}
              >
                {checked && (!disabled || (disabled && borderless)) && (
                  <span
                    className={cn(
                      'rounded-full !h-2 !w-2 min-w-[8px] min-h-[8px] bg-purple-700',
                      disabled && 'bg-neutral-90'
                    )}
                  />
                )}
              </span>
            )}
            {borderless && !disabled ? <StyledRadioHoverEl /> : null}
          </span>
        )}
        <div className="flex flex-col gap-1 w-full">
          <span
            id={`radiogroup-label-${option.label}`}
            className={cn(
              'font-medium text-base cursor-pointer leading-5',
              // Side-by-side options stay on one line from `sm` up, so the boxes keep the widths they
              // have on desktop. Below `sm` the label wraps: an unbreakable label sets the row's
              // minimum width, and on a phone three options pushed the page wider than the screen.
              orientation === 'horizontal' && 'sm:whitespace-nowrap'
            )}
          >
            {option.label}
          </span>
          {option.description && (
            <span className="text-sm text-neutral-500 cursor-pointer leading-5">{option.description}</span>
          )}
          {otherOptionInput}
        </div>
      </div>
    </li>
  );
};

type TVariable = Variable & {
  disabled?: boolean;
  Icon?: IconComponentType;
  colorStyles?: string;
  description?: string;
};
export interface IRadioGroupProps extends Pick<InputProps, 'onChange' | 'borderless' | 'value' | 'id'> {
  inlineTextInputProps?: InputProps;
  testId?: string;
  options: TVariable[];
  returnKey?: string;
  showVerticalLineBelow?: boolean;
  helperText?: React.ReactNode;
  mainTitle?: React.ReactNode;
  subTitle?: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  customOptionStyle?: string;
  hideIndicator?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  className?: string;
}
interface RadioOptionProps extends Omit<IRadioGroupProps, 'options'> {
  option: TVariable;
  index: number;
  hideIndicator?: boolean;
  isFullWidth?: boolean;
}

export const StyledRadioHoverEl = ({ className }: { className?: string }) => (
  <span
    className={cn(
      'h-10 w-10 min-h-[40px] min-w-[40px] rounded-full hidden place-items-center absolute bg-purple-50 group-hover:grid',
      className
    )}
  />
);
